import React, { useState, useEffect, useCallback, useMemo } from 'react';

import Spin from 'antd/es/spin';

import PayForPerformanceSettingsService from 'services/p4pSettings';

import { Title } from 'components/Text';
import { Row, Col } from 'components/layout';
import Button from 'components/Button';
import SettingsList from '../components/SettingsList';

const p4pSettingsSchema = [
  {
    name: 'pct_threshold',
    inputType: 'percentage',
    title: 'Percentage Threshold',
    min: 0,
    max: 1000,
  },
  {
    name: 'base_points',
    inputType: 'number',
    title: 'Base Points',
    min: 0,
    max: 1000,
  },
  {
    name: 'goal_weight',
    inputType: 'percentage',
    title: 'Goal Weight',
    min: 0,
    max: 100,
  },
  {
    name: 'attendance_weight',
    inputType: 'percentage',
    title: 'Attendance Weight',
    min: 0,
    max: 100,
  },
  {
    name: 'min_participation',
    inputType: 'percentage',
    title: 'Minimum Participation',
    min: 0,
    max: 100,
  },
  {
    name: 'attendance_multiplier',
    inputType: 'number',
    title: 'Attendance Multiplier',
    min: 0,
    max: 1000,
  },
];

const p4pSettingsService = new PayForPerformanceSettingsService();

const PayForPerformanceTab = ({ showMessage }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settingsValues, setSettingsValues] = useState([]);

  const modifiedSettingsValues = useMemo(() => {
    return settingsValues.filter(settingValue => !!settingValue.modified);
  }, [settingsValues]);

  useEffect(() => {
    setIsLoading(true);

    p4pSettingsService.loadAll()
      .then(data => {
        if (!data) return;

        setSettingsValues(data.map(item => {
          return {
            id: item.id,
            name: item.config_name,
            value: item.config_value,
            description: item.description,
            modified: false,
            locationId: item.location_id,
          };
        }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const onSaveChanges = useCallback(() => {
    setIsSubmitting(true);

    const updates = modifiedSettingsValues.map(settingValue => {
        return {
          id: settingValue.id,
          config_name: settingValue.name,
          config_value: settingValue.value,
          location_id: settingValue.locationId,
        };
    }, []);

    Promise.allSettled(
      updates.map(async (value) => {
        await p4pSettingsService.update(value);
      })
    ).then(() => {
      showMessage('success', 'Pay for Performance settings updated');
      setSettingsValues(settingsValues => settingsValues.map(config => {
        config.modified = false;
        return config;
      }));
    })
    .catch(() => {
      showMessage('error', 'Failed to save Pay for Performance settings');
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  }, [showMessage, modifiedSettingsValues]);

  if (isLoading) {
    return (
      <Spin
        size={30}
        style={{ width: '100%', padding: '36px' }}
      />
    );
  }

  return (
    <Row justify="center">
      <Col span={18}>
        <Title
          level={4}
          style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}
        >
          Pay for Performance
        </Title>

        <SettingsList
          settingsSchema={p4pSettingsSchema}
          settingsValues={settingsValues}
          setSettingsValues={setSettingsValues}
        />

        <Row justify="center" style={{ padding: '16px 0' }}>
          <Button
            type="primary" 
            loading={isSubmitting}
            disabled={modifiedSettingsValues.length === 0}
            size="large"
            onClick={onSaveChanges}
            style={{
              width: '350px'
            }}
          >
            Save Changes
          </Button>
        </Row>

      </Col>
    </Row>
  );
};

export default PayForPerformanceTab;
